import { FormattedEvent } from '@eventbrite/consumer-search.event-renderer';
import { getLastRecentSearchTerms, getLastRecentViewsList } from '../utils/utils';
import { useQuery } from '@tanstack/react-query';
import { Location } from '@/types';

export const useRecommendations = ({
    location,
    enabled,
}: {
    location: Location;
    enabled: boolean;
}) => {
    return useQuery({
        // The endpoint takes no placeId — it re-resolves location server-side from the `location` cookie on every
        // request — so placeId/isOnline ride in the key only to force a refetch. Without them, changing location on
        // the For-you tab leaves this cached for up to staleTime while usePromotedEvents (keyed the same way) fetches
        // immediately, mixing ads for the new place into recommendations for the old one.
        queryKey: ['for-you-recommendations', location.placeId, location.isOnline],
        queryFn: fetchRecommendations,
        staleTime: 10 * 60 * 1000, // 10 minutes, means data is considered fresh for 10 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes, means data is considered stale for 30 minutes
        retry: false,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,
        enabled,
    });
};

async function fetchRecommendations(): Promise<Bucket[]> {
    const recentSearches = getLastRecentSearchTerms(10);
    const recentViews = getLastRecentViewsList(10);
    const timeoutMs = 9000;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetch(`/home/api/search/recommendations/`, {
            method: 'POST',
            body: JSON.stringify({ recentSearches, recentViews }),
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            console.error('Failed to fetch recommendations:', response.statusText);
            throw new Error('Failed to fetch recommendations');
        }
        const data = await response.json();
        return data.buckets as Bucket[];
    } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
            throw new Error(`Recommendations request timed out after ${timeoutMs}ms`);
        }
        throw error;
    }
}

interface Bucket {
    events: FormattedEvent[];
    name: string;
}
